import { stripHtml } from '../../utils/security';
import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';

// Brand assets from About page
import icon from '../../images/icon/magic.svg';
import gImg1 from '../../images/logo-fff.png';
import ai1 from '../../images/portfolio/baraka-ai.jpg';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const BlogPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const [dbBlogs, setDbBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await fetch(`${API_URL}categories.php?type=blog`);
                const data = await res.json();
                if (Array.isArray(data)) setCategories(data);
            } catch (err) {
                console.error('Error fetching blog categories:', err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
        window.scrollTo(0, 0);
    }, [lang, i18n]);

    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}blogs.php?lang=${lang}`);
            const data = await response.json();
            if (Array.isArray(data)) {
                setDbBlogs(data);
            }
        } catch (error) {
            console.error('Error fetching blogs from DB:', error);
        }
        setLoading(false);
    }, [lang]);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    const blogItems = t('blog.items', { returnObjects: true });
    
    // Static blogs
    const staticBlogs = typeof blogItems === 'object' && blogItems !== null ? Object.keys(blogItems).map(key => ({
        id: key,
        ...blogItems[key]
    })) : [];

    // Merge DB blogs (taking priority by slug if duplicates exist)
    const blogs = [...dbBlogs, ...staticBlogs.filter(sb => !dbBlogs.find(db => db.slug === sb.id))].map(b => {
        let imageUrl = b.image;
        if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('static')) {
            imageUrl = API_URL + imageUrl;
        } else if (!imageUrl || imageUrl.startsWith('static')) {
            imageUrl = ai1;
        }

        return {
            ...b,
            id: b.slug || b.id, // normalize id
            image: imageUrl
        };
    });

    const filteredBlogs = selectedCategory === 'all' 
        ? blogs 
        : blogs.filter(b => b.category === selectedCategory);

    return (
        <Fragment>
            <Seo 
                title={`${t('navigation.blog')} | Orbitronix`}
                description={t('blog.subtitle')}
                url={`/${lang}/blog`}
            />
            <Layout>
                <main className="page_content overflow-hidden">
                    
                    {/* Modern Hero Section */}
                    <motion.section
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="page-title pt-150  pos-rel bg_img"
                    >
                        <div className="container">
                            <div className="page-title-wrap sd-title-wrap">
                                <div className="row mt-none-30 align-items-end">
                                    <div className="col-lg-9 mt-30">
                                        <motion.div 
                                            initial={{ opacity: 0, x: -50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.3 }}
                                            className="page-title-box"
                                        >
                                            <motion.span 
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: 0.5 }}
                                                className="sub-title"
                                            >
                                                <img src={icon} alt="" /> {t('navigation.blog')}
                                            </motion.span>
                                            <motion.h2 
                                                initial={{ opacity: 0, y: 30 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.7 }}
                                                className="title"
                                            >
                                                {t('blog.title')}
                                            </motion.h2>
                                            <p className="text-muted fs-5 mt-4 max-w-600">{t('blog.subtitle')}</p>
                                        </motion.div>
                                    </div>
                                    <div className="col-lg-3 mt-30 justify-content-center d-flex">
                                        <motion.div 
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.9, duration: 0.5 }}
                                            className="sd-right-img pos-rel d-none d-lg-block"
                                        >
                                            <img src={gImg1} alt="" />
                                        </motion.div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.section>

                    {/* Creative Blog Grid Section */}
                    <section className="blog-section-creative pt-85 cp-det-bg">
                        <div className="container">
                            {/* Dynamic Category Filter Pills */}
                            {categories.length > 0 && (
                                <div className="d-flex justify-content-center flex-wrap gap-2 mb-5">
                                    <button
                                        type="button"
                                        className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory('all')}
                                    >
                                        {lang === 'ka' ? 'ყველა' : 'All'}
                                    </button>
                                    {categories.map(cat => (
                                        <button
                                            type="button"
                                            key={cat.slug}
                                            className={`category-filter-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                                            onClick={() => setSelectedCategory(cat.slug)}
                                        >
                                            {lang === 'ka' ? cat.name_ka : (cat.name_en || cat.name_ka)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loading ? (
                                <div className="row justify-content-center py-5">
                                    <div className="col-auto">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="row">
                                    {filteredBlogs.map((blog, index) => {
                                        const matchingCat = categories.find(c => c.slug === blog.category);
                                        const catLabel = matchingCat 
                                            ? (lang === 'ka' ? matchingCat.name_ka : (matchingCat.name_en || matchingCat.name_ka))
                                            : (t('navigation.blog') || 'Blog');
                                        return (
                                        <div className="col-lg-4 col-md-6 mb-5" key={blog.id}>
                                            <motion.div 
                                                initial={{ opacity: 0, y: 30 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                                className="blog-card-creative"
                                            >
                                                <div className="blog-image">
                                                    <img src={blog.image} alt={blog.title} />
                                                    <div className="blog-date">
                                                        <span className="day">{blog.date?.split(' ')[1]?.replace(',', '') || '10'}</span>
                                                        <span className="month">{blog.date?.split(' ')[0] || 'May'}</span>
                                                    </div>
                                                    <div className="blog-overlay">
                                                        <Link to={`/${lang}/blog/${blog.id}`} className="view-btn">
                                                            <i className="fas fa-eye"></i>
                                                        </Link>
                                                    </div>
                                                </div>
                                                <div className="blog-content">
                                                    <div className="blog-meta">
                                                        <span className="author"><i className="far fa-user"></i> {blog.author}</span>
                                                        <span className="category"><i className="far fa-folder"></i> {catLabel}</span>
                                                    </div>
                                                    <h3 className="blog-title">
                                                        <Link to={`/${lang}/blog/${blog.id}`}>{blog.title}</Link>
                                                    </h3>
                                                    <p className="blog-summary">{stripHtml(blog.summary)}</p>
                                                    <div className="blog-footer">
                                                        <Link to={`/${lang}/blog/${blog.id}`} className="read-more-btn-creative">
                                                            <span>{t('blog.readMore')}</span>
                                                            <i className="fas fa-long-arrow-alt-right"></i>
                                                        </Link>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </div>
                                        );
                                    })}
                                    {filteredBlogs.length === 0 && !loading && (
                                        <div className="col-12 text-center py-5">
                                            <p className="text-muted">No blog posts found.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </main>
            </Layout>

            <style>{`
                .max-w-600 { max-width: 600px; }
                .category-filter-btn {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #cbd5e1;
                    padding: 8px 22px;
                    border-radius: 30px;
                    font-size: 0.88rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .category-filter-btn:hover {
                    background: rgba(108, 92, 231, 0.2);
                    border-color: rgba(108, 92, 231, 0.5);
                    color: #ffffff;
                }
                .category-filter-btn.active {
                    background: linear-gradient(135deg, #6c5ce7 0%, #4834d4 100%);
                    border-color: #6c5ce7;
                    color: #ffffff;
                    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.4);
                }
                .blog-section-creative { position: relative; min-height: 60vh; }
                .blog-card-creative { 
                    background: #080c27; 
                    backdrop-filter: blur(10px); 
                    border-radius: 20px; 
                    overflow: hidden; 
                    border: 1px solid rgba(255, 255, 255, 0.05); 
                    transition: all 0.4s ease; 
                    height: 100%; 
                    display: flex; 
                    flex-direction: column;
                }
                .blog-card-creative:hover { 
                    transform: translateY(-10px); 
                    border-color: rgba(108, 92, 231, 0.3); 
                    background: rgba(26, 26, 36, 0.8);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.4);
                }
                .blog-image { position: relative; width: 100%; height: 240px; overflow: hidden; }
                .blog-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.8s ease; }
                .blog-card-creative:hover .blog-image img { transform: scale(1.1); }
                
                .blog-date { 
                    position: absolute; 
                    top: 15px; 
                    right: 15px; 
                    background: rgba(108, 92, 231, 0.9); 
                    backdrop-filter: blur(5px);
                    color: #fff; 
                    padding: 8px 12px;
                    display: flex; 
                    flex-direction: column; 
                    align-items: center; 
                    justify-content: center; 
                    border-radius: 12px; 
                    font-weight: 700; 
                    z-index: 2; 
                }
                .blog-date .day { font-size: 16px; line-height: 1; }
                .blog-date .month { font-size: 10px; text-transform: uppercase; margin-top: 2px; opacity: 0.9; }
                
                .blog-overlay { 
                    position: absolute; 
                    inset: 0; 
                    background: rgba(12, 12, 20, 0.6); 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    opacity: 0; 
                    transition: all 0.3s ease; 
                }
                .blog-card-creative:hover .blog-overlay { opacity: 1; }
                .view-btn { 
                    width: 45px; 
                    height: 45px; 
                    background: #6c5ce7; 
                    color: #fff; 
                    border-radius: 50%; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    font-size: 16px; 
                    transform: translateY(20px); 
                    transition: all 0.3s ease; 
                }
                .blog-card-creative:hover .view-btn { transform: translateY(0); }
                
                .blog-content { padding: 30px; flex-grow: 1; display: flex; flex-direction: column; }
                .blog-meta { display: flex; gap: 15px; margin-bottom: 15px; font-size: 12px; color: #808080; }
                .blog-meta span i { color: #6c5ce7; margin-right: 5px; }
                
                .blog-title { font-size: 20px; font-weight: 700; line-height: 1.4; margin-bottom: 15px; }
                .blog-title a { color: #fff; text-decoration: none; transition: color 0.3s; }
                .blog-title a:hover { color: #6c5ce7; }
                
                .blog-summary { 
                    color: #a0a0a0; 
                    font-size: 14px; 
                    line-height: 1.6; 
                    margin-bottom: 25px; 
                    display: -webkit-box; 
                    -webkit-line-clamp: 3; 
                    -webkit-box-orient: vertical; 
                    overflow: hidden; 
                }
                
                .blog-footer { margin-top: auto; padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.05); }
                .read-more-btn-creative { 
                    display: inline-flex; 
                    align-items: center; 
                    gap: 10px; 
                    color: #6c5ce7; 
                    font-weight: 600; 
                    font-size: 13px; 
                    text-decoration: none; 
                    text-transform: uppercase; 
                    letter-spacing: 1px; 
                    transition: all 0.3s; 
                }
                .read-more-btn-creative i { transition: transform 0.3s ease; }
                .read-more-btn-creative:hover { color: #fff; }
                .read-more-btn-creative:hover i { transform: translateX(5px); }

                @media (max-width: 767px) { 
                    .blog-title { font-size: 18px; } 
                    .blog-content { padding: 20px; } 
                }
            `}</style>
        </Fragment>
    );
};

export default BlogPage;
